import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';

/**
 * Aviso amarelo com a Especificação Padrão do cliente, mostrado quando ela está marcada para
 * destaque. Usado no cadastro de cliente e no pedido.
 *
 * O HTML vem de `empresa.especificacoes`. O editor do cadastro só gera um punhado de tags, mas a
 * coluna também pode ter sido gravada direto pela API. Por isso o aviso mostra só o que o editor
 * sabe produzir: o sanitizer do Angular, que ainda roda no [innerHTML], tira script e on*, mas
 * deixa <img> e links "unsafe:". Não trocar por bypassSecurityTrustHtml nem por
 * <quill-view-html>, que desliga o sanitizer por padrão.
 */
@Component({
  selector: 'app-especificacao-cliente-destaque',
  templateUrl: './especificacao-cliente-destaque.component.html',
  styleUrl: './especificacao-cliente-destaque.component.scss'
})
export class EspecificacaoClienteDestaqueComponent implements OnChanges {
  @Input() especificacoes: string | null | undefined = null;
  @Input() destacar: boolean | null | undefined = false;
  /** Nome do cliente, para a linha "Vale para todos os pedidos de ..." (opcional). */
  @Input() cliente: string | null | undefined = null;

  public html = '';

  // As tags que o editor do cadastro produz (formatosEspecificacao do form-cliente).
  private static readonly TAGS_ESPECIFICACAO = new Set(['H1', 'H2', 'H3', 'P', 'BR', 'STRONG', 'EM', 'U', 'S', 'UL', 'OL', 'LI', 'A']);
  private static readonly TAGS_DESCARTADAS = 'script, style, template, noscript, iframe, frame, object, embed, svg, math, img, picture, video, audio, source, track, canvas, form, input, button, select, textarea';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['especificacoes']) {
      this.html = EspecificacaoClienteDestaqueComponent.limparHtml(this.especificacoes ?? null);
    }
  }

  private static limparHtml(html: string | null): string {
    if (!html) {
      return '';
    }

    // Documento inerte: o parse não executa script nem carrega imagem.
    const corpo = new DOMParser().parseFromString(html, 'text/html').body;
    corpo.querySelectorAll(EspecificacaoClienteDestaqueComponent.TAGS_DESCARTADAS).forEach(el => el.remove());
    corpo.querySelectorAll('*').forEach(el => {
      if (!EspecificacaoClienteDestaqueComponent.TAGS_ESPECIFICACAO.has(el.tagName)) {
        el.replaceWith(...Array.from(el.childNodes));
        return;
      }

      const href = el.tagName === 'A' ? el.getAttribute('href')?.trim() : null;
      Array.from(el.attributes).forEach(atributo => el.removeAttribute(atributo.name));
      // Mesmos protocolos que o link do Quill aceita.
      if (href && /^(https?:|mailto:|tel:)/i.test(href)) {
        el.setAttribute('href', href);
        el.setAttribute('target', '_blank');
        el.setAttribute('rel', 'noopener noreferrer');
      }
    });

    // O cabeçalho do cartão já diz "Especificação padrão do cliente". Um primeiro bloco que só
    // repete isso (é comum o texto começar assim) sai, para o título não aparecer duas vezes.
    const primeiro = corpo.firstElementChild;
    if (primeiro && EspecificacaoClienteDestaqueComponent.normalizar(primeiro.textContent)
        === 'especificacao padrao do cliente') {
      primeiro.remove();
    }

    return corpo.textContent?.trim() ? corpo.innerHTML : '';
  }

  /** Minúsculas, sem acento, espaços colapsados e sem ":" no fim. */
  private static normalizar(texto: string | null): string {
    return (texto ?? '')
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/:$/, '')
      .trim();
  }
}
